import type { SystemEvent, SystemEventType } from "..";
import { getWindowResolution } from "../utils/system";
import styles from "./SystemEvents.module.css";

export class SystemEventHandler {
  private resizeRemote = true;
  private localScaling: boolean | null = null;

  constructor(
    private videoElement: HTMLVideoElement | undefined,
    private getCursorScaleFactor?: (payload: {
      remoteResolutionEnabled: boolean;
    }) => void,
    private onSystemEvent?: (event: SystemEvent) => void,
  ) {}

  private parseValue(action: string): [SystemEventType, string] {
    const [type, value] = action.split(",");
    return [type as SystemEventType, value];
  }

  private updateVideoElementScalingStyle() {
    if (!this.videoElement) return;

    if (this.localScaling) {
      this.videoElement.style.width = "";
      this.videoElement.style.height = "";
      this.videoElement.className = styles.videoElementFullScale;
    } else {
      this.videoElement.className = styles.videoElementPositionOnly;

      const [width, height] = getWindowResolution();
      this.videoElement.style.width = `${width / window.devicePixelRatio}px`;
      this.videoElement.style.height = `${height / window.devicePixelRatio}px`;
    }
  }

  private handleResize(value: string): void {
    const isRemoteResize = value.toLowerCase() === "true";
    this.resizeRemote = isRemoteResize;

    if (!isRemoteResize && this.videoElement && this.localScaling === null) {
      this.localScaling = true;

      this.updateVideoElementScalingStyle();
    }
  }

  private handleResolution(value: string): void {
    if (this.resizeRemote) {
      const [width, height] = value.split("x").map((v) => parseInt(v));
      if (this.videoElement && !isNaN(width) && !isNaN(height)) {
        this.videoElement.style.width = `${width / window.devicePixelRatio}px`;
        this.videoElement.style.height = `${
          height / window.devicePixelRatio
        }px`;
      }
      this.getCursorScaleFactor?.({ remoteResolutionEnabled: true });
    }
  }

  private onLocalScaling(value: string) {
    this.localScaling = value.toLowerCase() === "true";
    this.updateVideoElementScalingStyle();
  }

  handleSystemMessage(action: string): void {
    const [type, value] = this.parseValue(action);

    switch (type) {
      case "resize":
        this.handleResize(value);
        this.onSystemEvent?.({ type, data: value.toLowerCase() === "true" });
        break;

      case "video_bitrate":
      case "audio_bitrate":
      case "framerate":
      case "encoder": {
        this.onSystemEvent?.({ type, data: value });
        break;
      }

      case "local_scaling":
        this.onLocalScaling(value);
        this.onSystemEvent?.({
          type,
          data: value.toLowerCase() === "true",
        });
        break;

      case "resolution":
        this.handleResolution(value);
        this.onSystemEvent?.({ type, data: value });
        break;
    }
  }
}
